import type { ReactNode, Ref } from 'react';
import { useResizableColumn } from '../lib/useResizableColumn';

/**
 * Values handed to the render-prop child of `<ResizableAside>`. Attach
 * `measureRef` to the scrollable list element, spread `rowAttr` onto each
 * row wrapper and `labelAttr` onto the text span that defines the row's
 * intrinsic width (icons and action buttons sit outside so the column
 * auto-fits the text, not the chrome).
 */
export interface ResizableAsideRender {
  measureRef: Ref<HTMLElement>;
  rowAttr: Record<string, string>;
  labelAttr: Record<string, string>;
}

interface Props {
  storageKey: string;
  min: number;
  max: number;
  defaultWidth: number;
  enabled: boolean;
  className?: string;
  /** Tailwind classes used for the non-resizable (mobile) fallback aside. */
  fallbackClassName?: string;
  contentSignature?: string | number;
  children: (ctx: ResizableAsideRender) => ReactNode;
}

export function ResizableAside({
  storageKey,
  min,
  max,
  defaultWidth,
  enabled,
  className,
  fallbackClassName,
  contentSignature,
  children,
}: Props) {
  const {
    width,
    mode,
    dragging,
    asideRef,
    measureRef,
    onHandlePointerDown,
    onHandleDoubleClick,
    onHandleKeyDown,
  } = useResizableColumn({
    storageKey,
    min,
    max,
    defaultWidth,
    enabled,
    contentSignature,
  });

  if (!enabled) {
    return (
      <aside className={fallbackClassName ?? className}>
        {children({ measureRef: () => {}, rowAttr: {}, labelAttr: {} })}
      </aside>
    );
  }

  return (
    <aside
      ref={asideRef}
      className={`relative ${className ?? ''}`}
      style={{ width, flex: `0 0 ${width}px` }}
    >
      {children({
        measureRef: measureRef as unknown as Ref<HTMLElement>,
        rowAttr: { 'data-resizable-row': '' },
        labelAttr: { 'data-resizable-label': '' },
      })}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-valuenow={width}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-label="Resize column"
        tabIndex={0}
        onPointerDown={onHandlePointerDown}
        onDoubleClick={onHandleDoubleClick}
        onKeyDown={onHandleKeyDown}
        className="group absolute top-0 right-0 z-10 h-full w-1.5 -mr-[3px] cursor-col-resize outline-none focus-visible:ring-1 focus-visible:ring-dragon-500"
      >
        <span
          className={`pointer-events-none absolute top-0 right-0 h-full w-px transition-colors ${
            dragging
              ? 'bg-dragon-500'
              : mode === 'manual'
                ? 'bg-ink-700 group-hover:bg-dragon-500'
                : 'bg-ink-800 group-hover:bg-dragon-500'
          }`}
        />
      </div>
    </aside>
  );
}
